package ai.jessie.web.projects

import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.FormStackStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.VisuallyHiddenStyle
import ai.jessie.web.theme.icon
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

// Steps 4–10, A1 and A5. Submitting only opens the confirmation; nothing is sent here. The
// buttons live in the slide-over footer and address this form by [PROJECT_FORM_ID].
@Composable
internal fun projectForm(state: ProjectUiState, actions: ProjectActions) {
    val draft = state.draft ?: return
    Form(
        attrs = {
            id(PROJECT_FORM_ID)
            addEventListener("submit") {
                it.preventDefault()
                actions.review()
            }
        }
    ) {
        Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
            Legend(attrs = VisuallyHiddenStyle.toAttrs()) { Text("Projektdaten") }
            Div(attrs = FormStackStyle.toAttrs()) {
                projectField(
                    ProjectField(
                        "name",
                        "Projektname (Pflichtfeld, höchstens 200 Zeichen)",
                        draft.name,
                        state.problems["name"],
                    )
                ) { value ->
                    actions.change { it.copy(name = value) }
                }
                projectField(
                    ProjectField(
                        "description",
                        "Beschreibung (optional, höchstens 500 Zeichen)",
                        draft.description,
                        state.problems["description"],
                    )
                ) { value ->
                    actions.change { it.copy(description = value) }
                }
                customerField(state, actions)
                Div(attrs = RowStyle.toAttrs()) {
                    projectCheckbox("billable", "Projekt ist verrechenbar", draft.billable) { value
                        ->
                        actions.change { it.copy(billable = value) }
                    }
                    projectCheckbox("active", "Projekt ist aktiv", draft.active) { value ->
                        actions.change { it.copy(active = value) }
                    }
                }
                positionFields(state, actions)
            }
        }
    }
}

@Composable
private fun customerField(state: ProjectUiState, actions: ProjectActions) {
    val draft = state.draft ?: return
    projectSelect(
        ProjectField(
            "customerId",
            if (draft.billable) "Kunde (Pflichtfeld für verrechenbare Projekte)"
            else "Kunde (optional)",
            draft.customerId,
            state.problems["customerId"],
        ),
        state.customerOptions(),
        emptyLabel = "Kein Kunde",
    ) { value ->
        actions.change { it.copy(customerId = value) }
    }
}

// A5: every position stays in the pending structure until the whole project is confirmed. Each
// position is a card shaped like the position slide-over (`13-PositionSlideOver.png`).
@Composable
private fun positionFields(state: ProjectUiState, actions: ProjectActions) {
    val draft = state.draft ?: return
    Fieldset(
        attrs =
            FieldsetStyle.toAttrs {
                id("project-positions")
                attr("tabindex", "-1")
            }
    ) {
        Legend(attrs = LegendStyle.toAttrs { style { property("padding", "0 0 12px") } }) {
            Text("Positionen (mindestens eine)")
        }
        Div(attrs = FormStackStyle.toAttrs()) {
            state.problems["positions"]?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
            draft.positions.forEachIndexed { index, row ->
                val change: ((PositionDraft) -> PositionDraft) -> Unit = { update ->
                    actions.change { it.copy(positions = it.positions.updated(index, update)) }
                }
                Div(attrs = CardStyle.toAttrs()) {
                    Div(attrs = CardBodyStyle.toAttrs()) {
                        positionLines(index, row, state, change)
                        // A stored position is removed only through its own explicit operation
                        // (A8, A9).
                        if (row.id == null)
                            Div(attrs = RowStyle.toAttrs()) {
                                themedButton(
                                    "Position ${index + 1} verwerfen",
                                    {
                                        actions.change {
                                            it.copy(
                                                positions =
                                                    it.positions.filterIndexed { i, _ ->
                                                        i != index
                                                    }
                                            )
                                        }
                                    },
                                    variant = ButtonVariant.GHOST,
                                    size = ButtonSize.SMALL,
                                    disabled = state.busy,
                                )
                            }
                    }
                }
            }
            Div(attrs = RowStyle.toAttrs()) {
                themedButton(
                    "Position hinzufügen",
                    { actions.change { it.copy(positions = it.positions + PositionDraft()) } },
                    variant = ButtonVariant.TINT,
                    disabled = state.busy,
                    leading = { icon("plus", size = 12) },
                )
            }
        }
    }
}

// Name, the type as selectable cards, price and VAT in one row, then the two states.
@Composable
private fun positionLines(
    index: Int,
    row: PositionDraft,
    state: ProjectUiState,
    change: ((PositionDraft) -> PositionDraft) -> Unit,
) {
    val prefix = "positions[$index]"
    val label = "Position ${index + 1}"
    fun field(name: String, text: String, value: String) =
        ProjectField("$prefix.$name", "$label: $text", value, state.problems["$prefix.$name"])
    projectField(field("name", "Name", row.name)) { value -> change { it.copy(name = value) } }
    projectChoice(field("type", "Art", row.type), positionChoices) { value ->
        change { it.copy(type = value) }
    }
    // BR-052: price and VAT exist only for a billable position.
    if (row.billable) {
        val stored =
            state.current?.positions.orEmpty().filter {
                row.id != null && it.details.id == row.id
            }
        Div(attrs = Grid2Style.toAttrs()) {
            projectField(
                field("unitPrice", "${priceLabel(row.type)} (zum Beispiel 180.00)", row.unitPrice),
                decimal = true,
            ) { value ->
                change { it.copy(unitPrice = value) }
            }
            projectSelect(
                field("vatRateId", "MWST-Satz", row.vatRateId),
                state.vatOptions(stored),
            ) { value ->
                change { it.copy(vatRateId = value) }
            }
        }
    }
    Div(attrs = RowStyle.toAttrs()) {
        projectCheckbox("$prefix.billable", "$label ist verrechenbar", row.billable) { value ->
            change { it.copy(billable = value) }
        }
        projectCheckbox("$prefix.active", "$label ist aktiv", row.active) { value ->
            change { it.copy(active = value) }
        }
    }
}

// BR-052: the same amount is an hourly rate or a fixed price, depending on the type. A getter, so
// that nothing is built at module initialisation.
private val positionChoices
    get() =
        listOf(
            ProjectChoice("EFFORT", "Aufwand", "Der Betrag ist ein Stundenansatz."),
            ProjectChoice("FIXED_PRICE", "Festpreis", "Der Betrag ist ein fester Preis."),
        )

// Handlers update the current draft, not the one captured at composition, so several inputs
// arriving before a recomposition (autofill, paste) do not overwrite each other.
private fun <T> List<T>.updated(index: Int, update: (T) -> T): List<T> = mapIndexed { i, old ->
    if (i == index) update(old) else old
}
